import {
    ArrowRightIcon,
    CartIcon,
    MenuIcon,
    PenIcon,
    PinIcon,
    QuestionnaireIcon,
    TrashIcon,
    UserIcon,
    WriteIcon,
} from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import React from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { DisplayStepOption, Experience, Field, FieldType, FieldVisibility } from "types";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { Translate } from "../../Common/Translate";
import { CatalogItemImage } from "@/components/Common/CatalogItemImage";
import { useInstalledPlugins } from "@/hooks/usePlugins";
import { defaultField } from "@/models/Form/Field";
import { CustomQuestions, getModalType, PredefinedQuestions } from "@/models/Form/QuestionnaireModalType";

interface QuestionFieldProps {
    field: Field;
    editable?: boolean;
    onDelete?: (field: Field) => void;
    onEdit?: (field: Field) => void;
    draggableProvided?: DraggableProvided;
    isMembership?: boolean;
    experience?: Experience;
}

type BadgeName = keyof Field;

interface BadgeInfo {
    key: BadgeName;
    name: string;
}

const badgeList: BadgeInfo[] = [
    { key: "displayStep", name: "" },
    { key: "optInByDefault", name: "Opt in by default" },
    { key: "organizerOnly", name: "" },
    { key: "required", name: "Required Field" },
    { key: "addToSavedViews", name: "Added to saved views" },
    { key: "roster", name: "Include in roster" },
];

export const QuestionField = ({
    field,
    onDelete,
    editable = true,
    onEdit,
    draggableProvided,
    isMembership,
    experience,
}: QuestionFieldProps) => {
    const { data: plugins } = useInstalledPlugins();

    const handleEdit = () => onEdit?.(field);
    const handleDelete = () => onDelete?.(field);
    const Icon = getIcon(field);

    const hasQuestionnairePlugin = plugins.some((plugin) => "questionnaire" in (plugin.abilities ?? {}));
    const isVisibilityFieldsDisplayed =
        !isMembership && hasQuestionnairePlugin && field.displayStep !== DisplayStepOption.BEFORE_PAYMENT;
    const isPredefined = PredefinedQuestions.some((info) => info.type === getModalType(field));

    const isAdults = field.visibility === FieldVisibility.ADULTS;
    const isMinors = field.visibility === FieldVisibility.MINORS;

    const isAddon = field.type === FieldType.ADDON;

    return (
        <div className="flex rounded-xl border border-gray-light bg-white" data-testid="question">
            {draggableProvided?.dragHandleProps && (
                <div className="flex items-center pl-4" {...draggableProvided.dragHandleProps}>
                    <MenuIcon size="medium" />
                </div>
            )}

            <div className="grow space-y-2 p-4">
                <div className="flex text-md font-bold">
                    <div className="flex grow items-center space-x-2">
                        {isAddon && field.catalogItem && experience ? (
                            <CatalogItemImage catalogItem={field.catalogItem} size="tiny" className="mr-0" />
                        ) : (
                            <Icon size="medium" />
                        )}
                        <Translate if={isPredefined} as="div" data-testid="question-label">
                            {field.label}
                        </Translate>
                    </div>
                    {editable && (
                        <div className="flex space-x-1">
                            <SecondaryButton size="small" data-testid="question-edit-button" onClick={handleEdit}>
                                <PenIcon />
                            </SecondaryButton>
                            <SecondaryButton size="small" data-testid="question-delete-button" onClick={handleDelete}>
                                <TrashIcon />
                            </SecondaryButton>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-y-2 space-x-2">
                    {badgeList.map((info) => {
                        const value = field[info.key];

                        if (!value) {
                            return null;
                        }

                        return (
                            <Badge key={info.key} color="secondary" data-testid="question-settings-tag">
                                {getDisplayBadgeLabel(info.key, field, info.name)}
                            </Badge>
                        );
                    })}
                    {isVisibilityFieldsDisplayed && (
                        <>
                            {isAdults && <Badge color="secondary">For Adult</Badge>}
                            {isMinors && <Badge color="secondary">For Minor</Badge>}
                        </>
                    )}
                    {field.type === FieldType.ADDON && <Badge color="secondary">Add-on</Badge>}
                </div>
            </div>
        </div>
    );
};

const getIcon = (field: Field) => {
    if (field.id === defaultField.id) {
        return UserIcon;
    }

    const modalType = getModalType(field);
    const info = PredefinedQuestions.concat(CustomQuestions).find((item) => item.type === modalType);

    return info?.icon ?? PinIcon;
};

const BeforePaymentBadge = () => {
    return (
        <span>
            <QuestionnaireIcon />
            <ArrowRightIcon size="tiny" className="p-0.5" />
            <CartIcon /> Before payment
        </span>
    );
};

const AfterPaymentBadge = () => {
    return (
        <span>
            <CartIcon />
            <ArrowRightIcon size="tiny" className="p-0.5" />
            <QuestionnaireIcon /> After payment
        </span>
    );
};

const getDisplayBadgeLabel = (key: string, field: Field, name: string) => {
    if (key === "displayStep") {
        switch (field.displayStep) {
            case DisplayStepOption.BEFORE_PAYMENT:
                return <BeforePaymentBadge />;
            case DisplayStepOption.WAIVER_SIGNING:
                return (
                    <span>
                        <WriteIcon /> In Waiver Workflow
                    </span>
                );
            case DisplayStepOption.AFTER_PAYMENT:
                return <AfterPaymentBadge />;
            default:
                return <AfterPaymentBadge />;
        }
    }
    if (key === "organizerOnly") {
        return field.organizerOnly ? "Ask Only Organizer" : "Ask All Attendees";
    }

    return name;
};
